import { useMemo } from "react";
import { useQuery } from "react-query";
import { useAuth0 } from "@auth0/auth0-react";
import { Text, Badge, Flex, Spinner } from "@radix-ui/themes";
import { ColumnDef } from "@tanstack/react-table";
import styles from "./TenantStatistics.module.scss";
import { formatPercent } from "@/v2/components/formatters/NumberFormatter";
import { useTenantApi } from "@/v2/entities/tenant/Tenant.api";
import {
    TenantStatisticsApiResponse,
    channelLabels,
    AgentChannel,
} from "@/v2/entities/tenant/Tenant.model";
import { SimpleTable } from "@/v2/components/table/SimpleTable";

function TenantStatistics() {
    const tenantApi = useTenantApi();
    const { user } = useAuth0();

    const { data: tenantData = [], isLoading, isError, error } =
        useQuery<TenantStatisticsApiResponse[]>({
            queryKey: ["admin-dashboard", "statistics", user?.sub],
            queryFn: tenantApi.getTenantsStatistics,
            cacheTime: 10 * 60 * 1000,
            refetchOnWindowFocus: false,
            enabled: !!user,
        });

    const columns = useMemo<ColumnDef<TenantStatisticsApiResponse>[]>(
        () => [
            {
                id: "tenantId",
                accessorKey: "tenantId",
                header: "Tenant",
                cell: ({ getValue }) => {
                    const value = getValue<string>();
                    return (
                        <Text size="3" weight="medium">
                            {value}
                        </Text>
                    );
                },
            },
            {
                id: "aiAgentsPublished",
                accessorKey: "aiAgentsPublished",
                header: "Published Agents",
                cell: ({ getValue }) => {
                    const value = getValue<number>();
                    return <Text size="2">{value}</Text>;
                },
            },
            {
                id: "aiAgentsChannels",
                accessorKey: "aiAgentsChannels",
                header: "Channels",
                cell: ({ getValue }) => {
                    const channels = getValue<AgentChannel[]>();
                    if (channels.length === 0) {
                        return (
                            <Text size="2" color="gray">
                                -
                            </Text>
                        );
                    }
                    return (
                        <Flex gap="1" wrap="wrap">
                            {channels.map((channel) => {
                                const channelKey = channel.channel;
                                const label =
                                    channelKey in channelLabels
                                        ? channelLabels[channelKey]
                                        : channelKey;
                                return (
                                    <Badge
                                        key={channelKey}
                                        size="1"
                                        variant="soft"
                                        color="blue"
                                    >
                                        {label}
                                    </Badge>
                                );
                            })}
                        </Flex>
                    );
                },
            },
            {
                id: "topicsReleased",
                accessorFn: (row) => `${row.topicsReleased} / ${row.topicsTotal}`,
                header: "Topics Released",
                cell: ({ row }) => {
                    const tenant = row.original;
                    return (
                        <Flex direction="column" gap="1">
                            <Text size="2">
                                {tenant.topicsReleased} / {tenant.topicsTotal}
                            </Text>
                            <Text size="1" color="gray">
                                {formatPercent(tenant.topicsReleased, tenant.topicsTotal)}
                            </Text>
                        </Flex>
                    );
                },
            },
            {
                id: "ticketsReleasedPercentage",
                accessorKey: "ticketsReleasedPercentage",
                header: "Released Tickets %",
                cell: ({ getValue }) => {
                    const value = getValue<number>();
                    const className =
                        value >= 50 ? styles.percentageGood : styles.percentageWarning;
                    return (
                        <Text size="2" weight="medium" className={className}>
                            {formatPercent(value)}
                        </Text>
                    );
                },
            },
            {
                id: "ticketsHandledByQuackFromReleasedPercentage",
                accessorKey: "ticketsHandledByQuackFromReleasedPercentage",
                header: "Quack Handled (Released) %",
                cell: ({ getValue }) => {
                    const value = getValue<number>();
                    const className =
                        value >= 80
                            ? styles.percentageGood
                            : value >= 50
                                ? styles.percentageWarning
                                : styles.percentageLow;
                    return (
                        <Text size="2" weight="medium" className={className}>
                            {formatPercent(value)}
                        </Text>
                    );
                },
            },
            {
                id: "ticketsHandledByQuackFromAllPercentage",
                accessorKey: "ticketsHandledByQuackFromAllPercentage",
                header: "Quack Handled (All) %",
                cell: ({ getValue }) => {
                    const value = getValue<number>();
                    const className =
                        value >= 80
                            ? styles.percentageGood
                            : value >= 50
                                ? styles.percentageWarning
                                : styles.percentageLow;
                    return (
                        <Text size="2" weight="medium" className={className}>
                            {formatPercent(value)}
                        </Text>
                    );
                },
            },
            {
                id: "skillsConfigured",
                accessorKey: "skillsConfigured",
                header: "Skills Configured",
                cell: ({ getValue }) => {
                    const value = getValue<number>();
                    return <Text size="2">{value}</Text>;
                },
            },
            {
                id: "skillsReleased",
                accessorKey: "skillsReleased",
                header: "Skills Released",
                cell: ({ getValue }) => {
                    const value = getValue<number>();
                    return <Text size="2">{value}</Text>;
                },
            },
        ],
        []
    );

    return (
        <Flex direction="column" gap="4" p="4">
            <Flex direction="row" justify="between" align="center">
                <Flex direction="column" gap="1">
                    <Text size="5" weight="bold">
                        Tenant Statistics
                    </Text>
                    <Text size="2" color="gray">
                        Overview of AI agents, topics, tickets, and skills across all tenants
                    </Text>
                </Flex>
            </Flex>

            {isError && (
                <Flex
                    direction="column"
                    gap="2"
                    p="4"
                    className={styles.errorBox}
                >
                    <Text size="3" weight="bold" color="red">
                        Error loading tenant statistics
                    </Text>
                    <Text size="2" color="red">
                        {error instanceof Error
                            ? error.message
                            : "Failed to fetch tenant statistics"}
                    </Text>
                </Flex>
            )}

            {isLoading && (
                <Flex justify="center" align="center" p="8">
                    <Spinner size="3" />
                </Flex>
            )}

            {!isLoading && !isError && tenantData.length === 0 && (
                <Flex
                    justify="center"
                    align="center"
                    p="8"
                    direction="column"
                    gap="2"
                >
                    <Text size="3" color="gray" weight="medium">
                        No tenant statistics available
                    </Text>
                    <Text size="2" color="gray">
                        There are no tenants with statistics to display
                    </Text>
                </Flex>
            )}

            {!isLoading && !isError && tenantData.length > 0 && (
                <div className="rounded-md overflow-hidden shadow-md border">
                    <SimpleTable data={tenantData} columns={columns} />
                </div>
            )}
        </Flex>
    );
}

export default TenantStatistics;

